// AvailabilityForm.jsx - Enhanced form component with fixed copy functionality
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { axiosInstance } from "../../../utils/axios";
import { useSpace } from "../../../contexts/SpaceContext";
import TimeSlotComponent from "./TimeSlotComponent";
import MonthView from "./MonthView";
import {
  CREATE_AVAILABILITY_MUTATION,
  UPDATE_AVAILABILITY_MUTATION,
} from "./graphql/queries";

import {
  Grid,
  TextField,
  DialogActions,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  Typography,
  FormControl,
  InputLabel,
  Select,
  Button,
  Snackbar,
  Alert,
  Box,
  Divider,
  Paper,
} from "@mui/material";

import { Add as AddIcon } from "@mui/icons-material";

// All timezone options
const TIMEZONE_OPTIONS = [
  { value: "Pacific/Midway", label: "Midway Island, Samoa (GMT-11:00)" },
  { value: "Pacific/Honolulu", label: "Hawaii (GMT-10:00)" },
  { value: "America/Anchorage", label: "Alaska (GMT-09:00)" },
  {
    value: "America/Los_Angeles",
    label: "Pacific Time (US & Canada) (GMT-08:00)",
  },
  { value: "America/Denver", label: "Mountain Time (US & Canada) (GMT-07:00)" },
  { value: "America/Chicago", label: "Central Time (US & Canada) (GMT-06:00)" },
  {
    value: "America/New_York",
    label: "Eastern Time (US & Canada) (GMT-05:00)",
  },
  { value: "America/Halifax", label: "Atlantic Time (Canada) (GMT-04:00)" },
  {
    value: "America/Argentina/Buenos_Aires",
    label: "Buenos Aires (GMT-03:00)",
  },
  { value: "Atlantic/South_Georgia", label: "Mid-Atlantic (GMT-02:00)" },
  { value: "Atlantic/Azores", label: "Azores (GMT-01:00)" },
  { value: "Europe/London", label: "London, Edinburgh, Dublin (GMT+00:00)" },
  { value: "Europe/Paris", label: "Paris, Brussels, Copenhagen (GMT+01:00)" },
  { value: "Europe/Helsinki", label: "Helsinki, Athens, Cairo (GMT+02:00)" },
  { value: "Asia/Kuwait", label: "Kuwait, Riyadh, Moscow (GMT+03:00)" },
  { value: "Asia/Dubai", label: "Abu Dhabi, Muscat (GMT+04:00)" },
  { value: "Asia/Kolkata", label: "Mumbai, New Delhi (GMT+05:30)" },
  { value: "Asia/Kathmandu", label: "Kathmandu (GMT+05:45)" },
  { value: "Asia/Dhaka", label: "Dhaka (GMT+06:00)" },
  { value: "Asia/Bangkok", label: "Bangkok, Jakarta (GMT+07:00)" },
  { value: "Asia/Shanghai", label: "Beijing, Singapore (GMT+08:00)" },
  { value: "Asia/Tokyo", label: "Tokyo, Seoul (GMT+09:00)" },
  { value: "Australia/Sydney", label: "Sydney, Melbourne (GMT+10:00)" },
  { value: "Pacific/Noumea", label: "New Caledonia (GMT+11:00)" },
  { value: "Pacific/Auckland", label: "Auckland (GMT+12:00)" },
  { value: "Pacific/Fakaofo", label: "Tokelau (GMT+13:00)" },
];

const ALL_DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const AvailabilityForm = ({ editingAvailability, handleClose, onSuccess }) => {
  const [availabilityName, setAvailabilityName] = useState(
    editingAvailability ? editingAvailability.name : ""
  );
  const [timezoneValue, setTimezoneValue] = useState(
    editingAvailability ? editingAvailability.timezone : ""
  );
  const [selectedDays, setSelectedDays] = useState(
    editingAvailability ? editingAvailability.days : []
  );
  const [availabilityTimeSlots, setAvailabilityTimeSlots] = useState(
    editingAvailability ? editingAvailability.timeSlots : []
  );
  const [availabilityOverrides, setAvailabilityOverrides] = useState(
    editingAvailability ? editingAvailability.overrides : []
  );
  const [editingOverrideIndex, setEditingOverrideIndex] = useState(null);
  const handleEditOverride = (index) => {
    // Store the index of the override being edited
    setEditingOverrideIndex(index);

    // Get the override
    const override = availabilityOverrides[index];

    // Parse the date string back to a dayjs object for the MonthView component
    const parsedDate = dayjs(override.date);

    // Open the dialog with pre-filled data
    setOpen(true);
  };

  // Get the spaceId from the URL query parameters
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const spaceId = queryParams.get("spaceId");

  // Alternatively, get it from the space context
  const { currentSpace } = useSpace();

  // New state to track time slots per day
  const [dayTimeSlots, setDayTimeSlots] = useState(() => {
    // Initialize with existing time slots if editing
    if (editingAvailability) {
      // First try to use the new dayTimeSlots structure if available
      if (
        editingAvailability.dayTimeSlots &&
        editingAvailability.dayTimeSlots.length > 0
      ) {
        const slotsMap = {};

        editingAvailability.dayTimeSlots.forEach((daySlot) => {
          if (daySlot.day && daySlot.slots) {
            slotsMap[daySlot.day] = daySlot.slots.map((slot) => ({
              timeStart: slot.timeStart,
              timeEnd: slot.timeEnd,
            }));
          }
        });

        return slotsMap;
      }

      // Fallback to the old structure if dayTimeSlots isn't available
      // This creates a best-effort mapping between days and time slots
      const slotsMap = {};
      const timeSlots = editingAvailability.timeSlots || [];
      const days = editingAvailability.days || [];

      // If we have both days and time slots, try to distribute them
      if (days.length > 0 && timeSlots.length > 0) {
        // Simple distribution - divide slots evenly among days
        const slotsPerDay = Math.ceil(timeSlots.length / days.length);

        days.forEach((day, index) => {
          const startIdx = index * slotsPerDay;
          const endIdx = Math.min(startIdx + slotsPerDay, timeSlots.length);

          slotsMap[day] = timeSlots.slice(startIdx, endIdx).map((slot) => ({
            timeStart: slot.timeStart,
            timeEnd: slot.timeEnd,
          }));
        });
      }

      return slotsMap;
    }

    return {};
  });

  const [open, setOpen] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Debugging - log state changes
  useEffect(() => {
    console.log("selectedDays updated:", selectedDays);
    console.log("dayTimeSlots updated:", dayTimeSlots);
  }, [selectedDays, dayTimeSlots]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      // Use the spaceId from URL or fallback to currentSpace.id
      const effectiveSpaceId = spaceId || currentSpace?.id;

      if (!effectiveSpaceId) {
        throw new Error(
          "No space ID available. Please ensure you're in a valid space."
        );
      }

      // Collect all time slots from all days for backward compatibility
      const allTimeSlots = [];

      // Build the new dayTimeSlots structure to maintain day-slot relationship
      const formattedDayTimeSlots = [];

      // Iterate through each selected day and its time slots
      selectedDays.forEach((day) => {
        if (dayTimeSlots[day] && dayTimeSlots[day].length > 0) {
          // Filter for valid slots (with both start and end times)
          const validSlots = dayTimeSlots[day]
            .filter((slot) => slot.timeStart && slot.timeEnd)
            .map((slot) => ({
              timeStart: slot.timeStart,
              timeEnd: slot.timeEnd,
            }));

          // Only add if we have valid slots
          if (validSlots.length > 0) {
            // Add to our dayTimeSlots structure
            formattedDayTimeSlots.push({
              day,
              slots: validSlots,
            });

            // Also add to the flat timeSlots array for backward compatibility
            validSlots.forEach((slot) => {
              allTimeSlots.push(slot);
            });
          }
        }
      });

      // Format overrides with proper timeEnd values for unavailable days
      const formattedOverrides = availabilityOverrides.map((override) => {
        if (
          override.isUnavailable ||
          override.timeSlots[0]?.timeStart === "Unavailable all day"
        ) {
          return {
            ...override,
            timeSlots: [
              {
                timeStart: "Unavailable all day",
                timeEnd: "Unavailable all day",
              },
            ],
            isUnavailable: true,
          };
        }

        // For regular overrides, ensure all time slots have both start and end times
        const validTimeSlots = override.timeSlots.filter(
          (slot) => slot.timeStart && slot.timeEnd
        );

        return {
          ...override,
          timeSlots: validTimeSlots,
          isUnavailable: false,
        };
      });

      const consolidatedOverrides = consolidateOverrides(formattedOverrides);

      // Create the properly formatted input for the API
      const input = {
        name: availabilityName,
        timezone: timezoneValue,
        days: selectedDays,
        timeSlots: allTimeSlots,
        dayTimeSlots: formattedDayTimeSlots,
        overrides: consolidatedOverrides,
        spaceId: effectiveSpaceId,
      };

      console.log(
        "Submitting availability with data:",
        JSON.stringify(input, null, 2)
      );

      let response;
      if (editingAvailability) {
        response = await axiosInstance.post("/graphql", {
          query: UPDATE_AVAILABILITY_MUTATION,
          variables: {
            id: editingAvailability.id,
            input,
          },
        });

        if (response.errors) {
          throw new Error(response.errors[0].message);
        }

        // Make sure we're accessing the correct path in the response
        const updatedAvailability =
          response.data.data?.updateAvailability ||
          response.data?.updateAvailability;

        if (updatedAvailability) {
          onSuccess("Availability updated successfully!");
          setAlert({
            open: true,
            message: "Availability updated successfully!",
            severity: "success",
          });

          // Keep alert open so user can see it
          setTimeout(() => {
            handleClose();
          }, 1000);
        } else {
          throw new Error("Unexpected response format");
        }
      } else {
        response = await axiosInstance.post("/graphql", {
          query: CREATE_AVAILABILITY_MUTATION,
          variables: {
            input,
          },
        });

        if (response.errors) {
          throw new Error(response.errors[0].message);
        }

        // Make sure we're accessing the correct path in the response
        const createdAvailability =
          response.data.data?.createAvailability ||
          response.data?.createAvailability;

        if (createdAvailability) {
          onSuccess("Availability created successfully!");

          // Set the alert to visible
          setAlert({
            open: true,
            message: "Availability created successfully!",
            severity: "success",
          });

          // Delay closing the form to allow the user to see the success message
          setTimeout(() => {
            handleClose();
          }, 1000);
        } else {
          throw new Error("Unexpected response format");
        }
      }
    } catch (error) {
      console.error("Error creating/updating availability:", error.message);
      setAlert({
        open: true,
        message: `Error: ${error.message}`,
        severity: "error",
      });
    }
  };

  const handleDaySelection = (day, slots, isChecked) => {
    console.log(`handleDaySelection for ${day}:`, {
      slots,
      isChecked,
      slotsLength: slots?.length,
    });

    if (isChecked) {
      // Add day to selectedDays if not already present
      setSelectedDays((prevDays) => {
        if (!prevDays.includes(day)) {
          console.log(`Adding ${day} to selectedDays`);
          return [...prevDays, day];
        }
        return prevDays;
      });

      // Store the time slots for this specific day
      // Create a deep copy to avoid reference issues
      const validSlots = Array.isArray(slots)
        ? slots.filter((slot) => slot.timeStart && slot.timeEnd)
        : [];

      console.log(
        `Setting ${validSlots.length} slots for ${day} in dayTimeSlots`
      );

      setDayTimeSlots((prev) => ({
        ...prev,
        [day]: validSlots.map((slot) => ({ ...slot })),
      }));
    } else {
      // Remove day from selectedDays
      console.log(`Removing ${day} from selectedDays`);
      setSelectedDays((prevDays) => prevDays.filter((d) => d !== day));

      // Remove time slots for this day
      console.log(`Removing time slots for ${day}`);
      setDayTimeSlots((prev) => {
        const updated = { ...prev };
        delete updated[day];
        return updated;
      });
    }
  };

  // THIS IS THE KEY FUNCTION FOR COPYING TIME SLOTS
  const applyDayTimeSlots = (targetDay, slots, isChecked) => {
    console.log(`applyDayTimeSlots for ${targetDay}:`, {
      slots,
      isChecked,
      slotsLength: slots?.length,
    });

    if (isChecked) {
      // Validate slots
      if (!Array.isArray(slots)) {
        console.error(
          `Invalid slots passed to applyDayTimeSlots for ${targetDay}:`,
          slots
        );
        return;
      }

      // Filter for valid slots with both start and end times
      const validSlots = slots.filter(
        (slot) =>
          slot && typeof slot === "object" && slot.timeStart && slot.timeEnd
      );

      console.log(`Found ${validSlots.length} valid slots for ${targetDay}`);

      if (validSlots.length === 0) {
        console.warn(`No valid time slots to apply to ${targetDay}`);
        return;
      }

      // Deep copy the slots to avoid reference issues
      const slotsCopy = validSlots.map((slot) => ({
        timeStart: slot.timeStart,
        timeEnd: slot.timeEnd,
      }));

      // First add the day to selectedDays if not already there
      setSelectedDays((prevDays) => {
        if (!prevDays.includes(targetDay)) {
          console.log(`Adding ${targetDay} to selectedDays`);
          return [...prevDays, targetDay];
        }
        return prevDays;
      });

      // Then set the time slots for this day
      // We need to use the callback form to ensure we're working with the latest state
      setDayTimeSlots((prevSlots) => {
        console.log(`Setting ${slotsCopy.length} time slots for ${targetDay}`);
        return {
          ...prevSlots,
          [targetDay]: slotsCopy,
        };
      });
    } else {
      // Handle removal
      setSelectedDays((prevDays) => prevDays.filter((d) => d !== targetDay));
      setDayTimeSlots((prevSlots) => {
        const updated = { ...prevSlots };
        delete updated[targetDay];
        return updated;
      });
    }
  };

  // Add debugging to track state changes
  useEffect(() => {
    console.log("selectedDays updated:", selectedDays);
  }, [selectedDays]);

  useEffect(() => {
    console.log("dayTimeSlots updated:", dayTimeSlots);
  }, [dayTimeSlots]);

  // Implement renderDayComponents for better clarity
  const renderDayComponents = () => {
    return ALL_DAYS.map((day) => (
      <TimeSlotComponent
        key={`timeSlot-${day}`} // Explicit key to help React with re-renders
        label={day}
        isAvailable={selectedDays.includes(day)}
        setIsAvailable={(slots, isChecked) =>
          handleDaySelection(day, slots, isChecked)
        }
        dayTimeSlots={dayTimeSlots[day] || []}
        currentDay={day}
        allDays={ALL_DAYS}
        setDayTimeSlots={applyDayTimeSlots}
      />
    ));
  };

  const handleAddOrUpdateOverride = (overrideData) => {
    // Check if this is an "unavailable all day" override
    const isUnavailableDay =
      overrideData.timeSlots[0]?.timeStart === "Unavailable all day";

    let formattedTimeSlots = overrideData.timeSlots;

    // For unavailable days, ensure timeEnd has a value
    if (isUnavailableDay) {
      formattedTimeSlots = [
        {
          timeStart: "Unavailable all day",
          timeEnd: "Unavailable all day",
        },
      ];
    }

    const formattedOverride = {
      ...overrideData,
      date: overrideData.date.format
        ? overrideData.date.format("YYYY-MM-DD")
        : overrideData.date,
      isUnavailable: Boolean(isUnavailableDay),
      timeSlots: formattedTimeSlots,
    };

    // Check if we're already editing an override
    if (editingOverrideIndex !== null) {
      setAvailabilityOverrides((prevOverrides) => {
        const newOverrides = [...prevOverrides];
        newOverrides[editingOverrideIndex] = formattedOverride;
        return newOverrides;
      });

      // Reset the editing state
      setEditingOverrideIndex(null);
    } else {
      // Check if there's already an override for this date
      const existingOverrideIndex = availabilityOverrides.findIndex(
        (override) => override.date === formattedOverride.date
      );

      if (existingOverrideIndex !== -1) {
        const existingOverride = availabilityOverrides[existingOverrideIndex];

        // If the existing override is marked as unavailable or the new one is
        if (existingOverride.isUnavailable || formattedOverride.isUnavailable) {
          // Ask user if they want to replace - since mixing unavailable with available doesn't make sense
          const confirmReplace = window.confirm(
            `There's already an override for ${dayjs(
              formattedOverride.date
            ).format("dddd, MMMM D, YYYY")}. ` +
              (existingOverride.isUnavailable
                ? "This date is currently marked as unavailable. Do you want to replace it with your new time slots?"
                : "Your new override will mark this day as unavailable. This will replace existing time slots. Continue?")
          );

          if (confirmReplace) {
            // Replace the existing override
            setAvailabilityOverrides((prevOverrides) => {
              const newOverrides = [...prevOverrides];
              newOverrides[existingOverrideIndex] = formattedOverride;
              return newOverrides;
            });
          } else {
            // User chose not to replace, so edit the existing override instead
            setEditingOverrideIndex(existingOverrideIndex);
            setOpen(true);
            return; // Exit early to prevent closing the dialog
          }
        } else {
          // Both are regular time slot overrides - APPEND the new time slots to existing ones
          setAvailabilityOverrides((prevOverrides) => {
            const newOverrides = [...prevOverrides];
            // Combine the time slots from both overrides
            const combinedTimeSlots = [
              ...existingOverride.timeSlots,
              ...formattedOverride.timeSlots,
            ];

            // Update the override with combined slots
            newOverrides[existingOverrideIndex] = {
              ...existingOverride,
              timeSlots: combinedTimeSlots,
            };

            return newOverrides;
          });

          // Show a confirmation message
          setAlert({
            open: true,
            message: `Added additional time slots to ${dayjs(
              formattedOverride.date
            ).format("dddd, MMMM D, YYYY")}`,
            severity: "success",
          });
        }
      } else {
        // Add as a new override if no existing override found for this date
        setAvailabilityOverrides([...availabilityOverrides, formattedOverride]);
      }
    }

    // Close the dialog
    setOpen(false);
  };

  const consolidateOverrides = (overrides) => {
    if (!overrides || overrides.length <= 1) {
      return overrides;
    }

    // Create a map to store consolidated overrides by date
    const overridesByDate = {};

    // Process each override
    overrides.forEach((override) => {
      const date = override.date;

      // Skip invalid overrides
      if (!date) return;

      // If this is the first override for this date, just store it
      if (!overridesByDate[date]) {
        overridesByDate[date] = { ...override };
      }
      // If there's already an override for this date, merge them
      else {
        const existingOverride = overridesByDate[date];

        // If either override marks the day as unavailable, the consolidated result is unavailable
        if (override.isUnavailable || existingOverride.isUnavailable) {
          existingOverride.isUnavailable = true;
          existingOverride.timeSlots = [
            {
              timeStart: "Unavailable all day",
              timeEnd: "Unavailable all day",
            },
          ];
        }
        // Otherwise, merge the time slots
        else {
          // Combine time slots from both overrides
          const allSlots = [
            ...existingOverride.timeSlots,
            ...override.timeSlots,
          ];

          // Filter out invalid slots and duplicates
          const uniqueSlots = [];
          const slotKeys = new Set();

          allSlots.forEach((slot) => {
            if (slot.timeStart && slot.timeEnd) {
              const slotKey = `${slot.timeStart}-${slot.timeEnd}`;
              if (!slotKeys.has(slotKey)) {
                slotKeys.add(slotKey);
                uniqueSlots.push({
                  timeStart: slot.timeStart,
                  timeEnd: slot.timeEnd,
                });
              }
            }
          });

          // Sort slots by start time
          uniqueSlots.sort((a, b) => {
            return a.timeStart.localeCompare(b.timeStart);
          });

          existingOverride.timeSlots = uniqueSlots;
        }
      }
    });

    // Convert the map back to an array
    return Object.values(overridesByDate);
  };

  const handleDeleteOverride = (index) => {
    setAvailabilityOverrides((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <form onSubmit={handleSubmit}>
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
        >
          {alert.message}
        </Alert>
      </Snackbar>

      <DialogContent>
        <Grid container spacing={3}>
          <Grid item md={6}>
            <TextField
              id="availabilityName"
              label="Availability Name"
              value={availabilityName}
              onChange={(e) => setAvailabilityName(e.target.value)}
              variant="outlined"
              fullWidth
              required
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid item md={6}>
            <FormControl fullWidth variant="outlined" required sx={{ mb: 2 }}>
              <InputLabel id="timezone-label" shrink>
                Timezone
              </InputLabel>
              <Select
                id="timezone"
                labelId="timezone-label"
                label="Timezone"
                value={timezoneValue}
                onChange={(e) => setTimezoneValue(e.target.value)}
                displayEmpty
                notched
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <span style={{ color: "#9e9e9e" }}>Select Timezone</span>
                    );
                  }
                  const option = TIMEZONE_OPTIONS.find(
                    (opt) => opt.value === selected
                  );
                  return option ? option.label : selected;
                }}
              >
                {TIMEZONE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="medium">
            Set Availability
          </Typography>
          <Typography color="textSecondary" variant="body2" sx={{ mb: 2 }}>
            Set when you are available so that you can be booked
          </Typography>

          <Paper sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {ALL_DAYS.map((day) => (
                  <TimeSlotComponent
                    key={day}
                    label={day}
                    isAvailable={selectedDays.includes(day)}
                    setIsAvailable={(slots, isChecked) =>
                      handleDaySelection(day, slots, isChecked)
                    }
                    dayTimeSlots={dayTimeSlots[day] || []}
                    currentDay={day}
                    allDays={ALL_DAYS}
                    setDayTimeSlots={applyDayTimeSlots}
                  />
                ))}
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* Date Overrides Section */}
        <Box sx={{ mt: 4, mb: 2 }}>
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="subtitle1" fontWeight="medium">
              Date Overrides
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setOpen(true)}
              startIcon={<AddIcon />}
            >
              Add an override
            </Button>
          </Grid>

          {/* List of Date Overrides */}
          {availabilityOverrides.length === 0 ? (
            <Typography
              color="textSecondary"
              variant="body2"
              sx={{ mt: 2, mb: 2 }}
            >
              No date overrides set
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {availabilityOverrides.map((override, index) => (
                <Grid key={index} item xs={12} md={6} lg={4}>
                  <Paper
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold">
                      {typeof override.date === "string"
                        ? dayjs(override.date).format("dddd, MMMM D, YYYY")
                        : override.date.format("dddd, MMMM D, YYYY")}
                    </Typography>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        color={
                          override.isUnavailable ? "error.main" : "success.main"
                        }
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        {override.isUnavailable
                          ? "Unavailable all day"
                          : override.timeSlots
                              .filter((slot) => slot.timeStart && slot.timeEnd)
                              .map(
                                (slot) => `${slot.timeStart} - ${slot.timeEnd}`
                              )
                              .join(", ")}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 1,
                        gap: 1,
                      }}
                    >
                      <Button
                        size="small"
                        color="primary"
                        onClick={() => handleEditOverride(index)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleDeleteOverride(index)}
                      >
                        Remove
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={
            !availabilityName || !timezoneValue || selectedDays.length === 0
          }
        >
          {editingAvailability ? "Update Availability" : "Create Availability"}
        </Button>
      </DialogActions>

      {/* Dialog for Adding Override */}
      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
          setEditingOverrideIndex(null); // Reset editing state when dialog is closed
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {editingOverrideIndex !== null
            ? "Edit Date Override"
            : "Select the dates to override"}
        </DialogTitle>
        <DialogContent>
          <MonthView
            onAddOverride={handleAddOrUpdateOverride}
            initialOverride={
              editingOverrideIndex !== null
                ? availabilityOverrides[editingOverrideIndex]
                : null
            }
          />
        </DialogContent>
      </Dialog>
    </form>
  );
};

export default AvailabilityForm;
